import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireBrands?: boolean
  requireAuth?: boolean
}

export function ProtectedRoute({ children, requireBrands = true, requireAuth = true }: ProtectedRouteProps) {
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    let mounted = true

    const checkAuth = async () => {
      try {
        // console.log(`[ProtectedRoute Debug] Checking auth for path: ${location.pathname}`)
        // console.log(`[ProtectedRoute Debug] requireAuth: ${requireAuth}, requireBrands: ${requireBrands}`)
        
        // Check if we have a valid session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        
        if (sessionError) {
          console.error('[ProtectedRoute Debug] Session error:', sessionError)
          if (requireAuth) {
            console.log('[ProtectedRoute Debug] Redirecting to / due to session error')
            window.location.href = '/'
          } else {
            setLoading(false)
          }
          return
        }
        
        if (!session) {
          console.log('[ProtectedRoute Debug] No session found')
          if (requireAuth) {
            console.log('[ProtectedRoute Debug] Redirecting to / due to no session')
            window.location.href = '/'
          } else {
            setLoading(false)
          }
          return
        }

        console.log('[ProtectedRoute Debug] Valid session found:', session.user?.id)

        // Check user role using the current_user_role view in basejump schema
        const { error: roleError } = await supabase
          .schema('basejump')
          .from('current_user_role')
          .select('role')
          .single()

        if (roleError && roleError.code !== 'PGRST116') { // Ignore "no rows returned" error
          console.error('ProtectedRoute: Role check error:', roleError)
          if (requireAuth) {
            window.location.href = '/'
          } else {
            setLoading(false)
          }
          return
        }

        // Special case for invitation flow - don't redirect if we're in the invitation acceptance process
        const isInvitationFlow = location.pathname.includes('/process-invitation') || 
                                location.pathname.includes('/invitations/accept');
                                
        if (isInvitationFlow) {
          console.log('[ProtectedRoute Debug] Detected invitation flow, skipping brand check');
          if (mounted) {
            setLoading(false);
          }
          return;
        }
        
        // Allow ALL users to access settings/billing regardless of onboarding or account status
        const isSettingsRoute = location.pathname.includes('/settings');
        
        if (isSettingsRoute) {
          console.log('[ProtectedRoute Debug] User accessing settings, allowing access without brand/onboarding check');
          if (mounted) {
            setLoading(false);
          }
          return;
        }
        
        // Only check brands if requireBrands is true and not in invitation flow
        if (requireBrands) {
          try {
            // console.log('[ProtectedRoute Debug] Checking brands...')
            const { data: brands, error: brandsError } = await supabase
              .from('brands')
              .select('id')
              .neq('status', 'DELETED')  // Only exclude deleted brands
              .limit(1)

            if (brandsError) {
              console.error('[ProtectedRoute Debug] Brands check error:', brandsError)
              navigate('/')
              return
            }

            if (!brands || brands.length === 0) {
              console.log('[ProtectedRoute Debug] No brands found via account, checking for direct access brands')
              
              // Check if user has brands through direct access (invited users)
              const { data: directAccessBrands } = await supabase
                .from('brand_access')
                .select('brand_id')
                .eq('user_id', session.user.id)
                .limit(1);
                
              if (directAccessBrands && directAccessBrands.length > 0) {
                console.log('[ProtectedRoute Debug] Found brands via direct access, user is invited but may need onboarding')
                // Don't redirect invited users, let them continue
                if (mounted) {
                  setLoading(false);
                }
                return;
              }
              
              // No brands at all, check onboarding status
              console.log('[ProtectedRoute Debug] No brands found anywhere, checking onboarding status')
              
              // Check if onboarding is completed before redirecting to onboarding
              const { data: onboardingStatus } = await supabase
                .schema('marketing_and_customer_success')
                .from('profiles')
                .select('onboarding_completed')
                .eq('id', session.user.id)
                .limit(1)
                .single()

              if (onboardingStatus?.onboarding_completed) {
                console.log('[ProtectedRoute Debug] Onboarding completed but no brands found, redirecting to brand creation')
                navigate('/onboarding?step=brand')
                return
              } else {
                console.log('[ProtectedRoute Debug] Onboarding not completed, redirecting to onboarding')
                navigate('/onboarding')
                return
              }
            }
            
            console.log('[ProtectedRoute Debug] Brands found:', brands.length)
          } catch (error) {
            console.error('[ProtectedRoute Debug] Brands check failed:', error)
            navigate('/')
            return
          }
        }

        if (mounted) {
          setLoading(false)
        }
      } catch (error) {
        console.error('ProtectedRoute: Auth check failed:', error)
        if (requireAuth) {
          navigate('/')
        } else {
          setLoading(false)
        }
      }
    }

    checkAuth()

    return () => {
      mounted = false
    }
  }, [navigate, requireBrands, requireAuth, location])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return <>{children}</>
} 